import React, { useEffect, useRef, useState } from "react";
import { Model, type SurveyModel } from "survey-core";
import { DefaultLight } from "survey-core/themes";
import { Survey } from "survey-react-ui";
import "survey-core/survey-core.css";

import Spinner from "shared/components/common/Spinner";
import {
  type InitializeSurveyModelProps,
  initializeSurveyModel,
} from "shared/components/survey-js-form/preview/initializeSurveyModel";
import { removeHTMLTags } from "shared/components/survey-js-form/utils/removeHTMLTags";
import { useCustomSession } from "shared/context/CustomSessionContext";
import { type SessionView } from "shared/mappers/database/session/session";

const newModel: SurveyModel = new Model();
newModel.applyTheme(DefaultLight);

export const surveyJSupdateOnCompleteHtml = (
  sender: SurveyModel,
  completedHtmlToSet: string,
) => {
  //Doing both setPropertyValue and updateSurvey seems to be required or else it doesnt detect the change
  sender.setPropertyValue("completedHtml", completedHtmlToSet);
  sender.updateSurvey({
    completedHtml: completedHtmlToSet,
  });
};

const formSubmittingSpinnerHtmlString =
  "<br/><div class='inline-block animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite] w-4 h-4' style='border-color: blue; border-right-color: transparent;'></div>";

export const surveyJSAddSpinnerToCompletedHtml = (sender: SurveyModel) => {
  const completedHtmlToSet =
    sender.completedHtml + formSubmittingSpinnerHtmlString;
  surveyJSupdateOnCompleteHtml(sender, completedHtmlToSet);
};

export const surveyJSRemoveSpinnerFromCompletedHtml = (sender: SurveyModel) => {
  const completedHtmlToSet = sender.completedHtml.replace(
    formSubmittingSpinnerHtmlString,
    "",
  );
  surveyJSupdateOnCompleteHtml(sender, completedHtmlToSet);
};

export interface SurveyJSFormComponentProps
  extends React.ComponentProps<typeof Survey>,
    Omit<InitializeSurveyModelProps, "session"> {
  session?: SessionView;
  className?: string;
  overrideTitle?: string;
  overrideDescription?: string;
  deviceType?: string;
}

const SurveyJSFormComponent = ({
  onComplete,
  ...props
}: SurveyJSFormComponentProps) => {
  const initializedRef = useRef(false);
  const modelRef = useRef<SurveyModel | null>(null);
  const session = useCustomSession();
  const [isReady, setIsReady] = useState(false);
  const isHeaderStickyEnabled = props.formDesign?.isHeaderSticky === true;

  const applyStickyHeaderStyles = (model: SurveyModel, isSticky: boolean) => {
    document.body.classList.toggle("header-sticky-mode", isSticky);

    if (!isSticky) {
      return;
    }

    const currentHeaderStyles = model.css?.header || "";
    model.css = {
      ...model.css,
      header:
        `${currentHeaderStyles} sticky top-0 left-0 right-0 z-[10] bg-white shadow-md p-4`.trim(),
    };
  };

  useEffect(() => {
    const initializeModel = async () => {
      const newModel = await initializeSurveyModel({
        onComplete,
        ...props,
        session,
      });

      applyStickyHeaderStyles(newModel, isHeaderStickyEnabled);

      if (props.overrideTitle) {
        newModel.title = props.overrideTitle;
      }
      if (props.overrideDescription) {
        newModel.description = props.overrideDescription;
      }
      newModel.setVariable("profile", session?.profile);
      newModel.getAllQuestions().forEach((question) => {
        if (question.title.includes("<p>")) {
          question.title = removeHTMLTags(question.title);
        }
      });
      modelRef.current = newModel;
      initializedRef.current = true;
      setIsReady(true);
    };

    initializeModel();

    return () => {
      if (modelRef.current) {
        modelRef.current.dispose();
        modelRef.current = null;
      }
      initializedRef.current = false;
      setIsReady(false);
    };
  }, []);

  if (isReady) {
    return (
      <div className={props.className}>
        <Survey
          {...props}
          className={
            isHeaderStickyEnabled
              ? "survey-tight survey-container"
              : "survey-tight"
          }
          model={modelRef.current!}
        />
      </div>
    );
  }
  return <Spinner />;
};

SurveyJSFormComponent.displayName = "SurveyJSFormComponent";
export default SurveyJSFormComponent;
